import { FiShuffle } from 'react-icons/fi';

export const mixedConditionalsData = {
  courseId: 'mixed-conditionals',
  courseType: 'grammar',
  title: 'Mixed Conditionals',
  icon: FiShuffle,
  overview: {
    impact: 'Combine time references for sophisticated expression',
    focus: 'Mixing second and third conditional structures',
    uses: 'Speaking Part 3, Writing Task 2',
    duration: '25 minutes'
  },
  sections: [
    {
      title: 'Past Cause → Present Result',
      content: `<p><strong>If + past perfect, would + base verb</strong></p><p>An unreal past action has a present result.</p>`,
      examples: [
        {
          label: 'Mixed',
          text: 'If I had studied medicine, I would be a doctor now.'
        },
        {
          label: 'Explained',
          text: '(I didn\'t study medicine → I\'m not a doctor)'
        }
      ]
    },
    {
      title: 'Present Cause → Past Result',
      content: `<p><strong>If + past simple, would have + past participle</strong></p><p>A general truth about someone affected a past situation.</p>`,
      examples: [
        {
          label: 'Mixed',
          text: 'If she weren\'t so shy, she would have spoken up at the meeting.'
        },
        {
          label: 'Explained',
          text: '(She IS shy → she DIDN\'T speak up)'
        }
      ]
    }
  ],
  exercises: [
    {
      title: 'Identify the Mixed Conditional',
      type: 'select',
      instructions: 'What type of conditional is each sentence?',
      questions: [
        {
          text: 'If I had saved more money, I would own a house now.',
          options: [
            'Past cause → present result',
            'Present cause → past result',
            'Third conditional'
          ],
          answer: 'Past cause → present result',
          explanation: 'Past perfect (had saved) + would + base verb (own) = past→present.'
        },
        {
          text: 'If he were more careful, he wouldn\'t have crashed the car.',
          options: [
            'Present cause → past result',
            'Past cause → present result',
            'Second conditional'
          ],
          answer: 'Present cause → past result',
          explanation: 'Past simple (were) + would have + pp (crashed) = present→past.'
        }
      ]
    }
  ],
  tips: [
    'Past→present: If + past perfect, would + base verb',
    'Present→past: If + past simple, would have + pp',
    'Shows sophisticated grammar range for Band 7+',
    'Use in Part 3 for complex hypothetical reasoning'
  ],
  dailyPractice: [
    'Practice mixed conditionals concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Mixed Conditionals: Combining second and third conditional for cross-time hypotheticals. Shows advanced grammar range in IELTS.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...mixedConditionalsData,
      title: mixedConditionalsData.title + ' (Foundations)',
      overview: { ...mixedConditionalsData.overview, duration: '15-20 minutes' },
      sections: mixedConditionalsData.sections.slice(0, Math.max(2, Math.ceil(mixedConditionalsData.sections.length / 2))),
      exercises: mixedConditionalsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...mixedConditionalsData,
      title: mixedConditionalsData.title + ' (Advanced)',
      overview: { ...mixedConditionalsData.overview, duration: '30-35 minutes' },
      sections: mixedConditionalsData.sections,
      exercises: mixedConditionalsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(mixedConditionalsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return mixedConditionalsData;
}
